import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { API } from '@/utils/constants';

/**
 * Address Autocomplete Component
 * Uses Google Places API (via backend proxy) for Australian address lookup
 * 
 * @param {Object} props
 * @param {string} props.value - Current input value
 * @param {Function} props.onChange - Callback when input changes
 * @param {Function} props.onAddressSelect - Callback when address is selected, receives parsed address data
 * @param {string} props.placeholder - Input placeholder
 * @param {boolean} props.disabled - Whether input is disabled
 */
export const AddressAutocomplete = ({ 
  value, 
  onChange, 
  onAddressSelect, 
  placeholder = "Start typing address...",
  disabled = false,
  ...props 
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isConfigured, setIsConfigured] = useState(null);
  const [internalValue, setInternalValue] = useState(value || '');
  const debounceTimer = useRef(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Sync internal value with external value only when external changes from parent (not from typing)
  useEffect(() => {
    if (value !== internalValue && document.activeElement !== inputRef.current) {
      setInternalValue(value || '');
    }
  }, [value]);

  // Check if Google Places is configured on mount
  useEffect(() => {
    const checkConfiguration = async () => {
      try {
        const response = await axios.get(`${API}/address/settings`);
        setIsConfigured(response.data.google_places?.has_api_key || false);
      } catch (error) {
        setIsConfigured(false);
      }
    };
    checkConfiguration();
  }, []);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async (input) => {
    if (!input || input.length < 3 || !isConfigured) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/address/autocomplete`, {
        params: { input }
      });
      setSuggestions(response.data.predictions || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Address autocomplete error:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    onChange(newValue);

    // Debounce API calls
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 350);
  };

  const handleSelectAddress = async (prediction) => {
    setShowSuggestions(false);
    setSuggestions([]);

    try {
      const response = await axios.get(`${API}/address/details/${prediction.place_id}`);
      const addressData = response.data;
      
      // Update the input with the street address
      const newAddress = addressData.address_line1 || prediction.main_text;
      setInternalValue(newAddress);
      onChange(newAddress);
      
      // Pass full address data to parent
      if (onAddressSelect) {
        onAddressSelect({
          address_line1: addressData.address_line1,
          suburb: addressData.suburb,
          postcode: addressData.postcode,
          state: addressData.state
        });
      }
    } catch (error) {
      console.error('Error getting address details:', error);
      // Fallback: just use the description
      setInternalValue(prediction.main_text);
      onChange(prediction.main_text);
    }
  };

  // If not configured, render a simple input
  if (isConfigured === false) {
    return (
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        {...props}
      />
    );
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <Input
        ref={inputRef}
        type="text"
        value={internalValue}
        onChange={handleInputChange}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        {...props}
      />
      
      {loading && (
        <div style={{
          position: 'absolute',
          right: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: '12px',
          color: '#666'
        }}>
          ...
        </div>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div 
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '0.375rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            maxHeight: '300px',
            overflowY: 'auto'
          }}
          onMouseDown={(e) => e.preventDefault()} // Prevent input blur when clicking suggestions
        >
          {suggestions.map((prediction, index) => (
            <div
              key={prediction.place_id || index}
              onClick={() => handleSelectAddress(prediction)}
              style={{
                padding: '0.75rem 1rem',
                cursor: 'pointer',
                borderBottom: index < suggestions.length - 1 ? '1px solid #f1f5f9' : 'none',
                transition: 'background-color 0.15s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
            >
              <div style={{ fontWeight: '500', color: '#1e293b' }}>
                {prediction.main_text}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                {prediction.secondary_text}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
